// src/dashboard/pages/teacher/Categories/components/CategoriesDistributionPanel.jsx

import React from "react";

function GenderBadge({ genre }) {
  const isFemale = genre === "Féminin";

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
        isFemale
          ? "bg-pink-50 text-pink-700 ring-pink-200 dark:bg-pink-500/10 dark:text-pink-300 dark:ring-pink-500/20"
          : "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20"
      }`}
    >
      {genre}
    </span>
  );
}

export default function CategoriesDistributionPanel({
  rows = [],
  counts = {},
  onOpenMembers,
}) {
  return (
    <div className="mt-5">
      <div className="hidden md:block">
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/60">
          <table className="min-w-[1100px] w-full">
            <thead className="sticky top-0 z-20">
              <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                <th className="px-4 py-3 text-left font-bold">
                  Catégorie d’âge
                </th>
                <th className="px-4 py-3 text-left font-bold">Genre</th>
                <th className="px-4 py-3 text-left font-bold">
                  Années de naissance
                </th>
                <th className="px-4 py-3 text-left font-bold">
                  Catégories de poids
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {rows.map((row) => (
                <tr
                  key={`${row.ageCategory}-${row.genre}`}
                  className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                >
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900 dark:text-white">
                      {row.label}
                    </div>

                    {row.isMorphological ? (
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Groupes morphologiques par poids réel.
                      </p>
                    ) : null}
                  </td>

                  <td className="px-4 py-3">
                    <GenderBadge genre={row.genre} />
                  </td>

                  <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                    {row.annees}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {row.brackets.length === 0 ? (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                          Aucun poids saisi
                        </span>
                      ) : (
                        row.brackets.map((bracket) => {
                          const key = `${row.ageCategory}|${row.genre}|${bracket}`;
                          const count = counts[key] || 0;
                          const hasMembers = count > 0;

                          return hasMembers ? (
                            <button
                              key={bracket}
                              type="button"
                              onClick={() => onOpenMembers({ row, bracket })}
                              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                            >
                              {bracket}
                              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1 text-[10px] text-white">
                                {count}
                              </span>
                            </button>
                          ) : (
                            <span
                              key={bracket}
                              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                            >
                              {bracket}
                            </span>
                          );
                        })
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    Aucune catégorie ne correspond aux filtres sélectionnés.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3 md:hidden">
        {rows.map((row) => (
          <article
            key={`mobile-${row.ageCategory}-${row.genre}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/60"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                  {row.label}
                </h3>

                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Années : {row.annees}
                </p>
              </div>

              <GenderBadge genre={row.genre} />
            </div>

            {row.isMorphological ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Groupes morphologiques par poids réel.
              </p>
            ) : null}

            <div className="mt-3 flex flex-wrap gap-2">
              {row.brackets.length === 0 ? (
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                  Aucun poids saisi
                </span>
              ) : (
                row.brackets.map((bracket) => {
                  const key = `${row.ageCategory}|${row.genre}|${bracket}`;
                  const count = counts[key] || 0;
                  const hasMembers = count > 0;

                  return hasMembers ? (
                    <button
                      key={bracket}
                      type="button"
                      onClick={() => onOpenMembers({ row, bracket })}
                      className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                    >
                      {bracket}
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1 text-[10px] text-white">
                        {count}
                      </span>
                    </button>
                  ) : (
                    <span
                      key={bracket}
                      className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                    >
                      {bracket}
                    </span>
                  );
                })
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}